import { createContext, FC, useCallback, useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { authDataSelector } from 'modules/login/api'
import { TOrderCompany, TSelectCustomer } from 'modules/order/types'
import { RadioSwitchValue, TOrderCreatePartnerFormData } from 'modules/order/types/orderCreateTypes'
import { OrderItemClient } from 'schema'

import { orderCreateContextInitial } from './constants'
import { CustomerContact, OrderCreateContextType, OrderCreateProviderProps } from './types'

const OrderCreateContext = createContext<OrderCreateContextType>(orderCreateContextInitial)

const OrderCreateProvider: FC<OrderCreateProviderProps> = ({ children }) => {
  const [activeCustomerId, setActiveCustomerId] = useState('')
  const [activeCustomer, setActiveCustomer] = useState<TOrderCompany>()
  const [selectedCustomer, setSelectedCustomer] = useState<TSelectCustomer>()
  const [isContactsByCustomerList, setIsContactsByCustomerList] = useState(false)
  const [isActiveCustomerAddContactAccess, setIsActiveCustomerAddContactAccess] = useState(false)
  const [isShowAttachBtn, setIsShowAttachBtn] = useState(true)
  const [isAttachBlockFullHeight, setIsAttachBlockFullHeight] = useState(false)

  const user = useSelector(authDataSelector)
  const userContact = useMemo(
    () => ({
      firstname: user?.firstname || '',
      lastname: user?.lastname || '',
      secondname: user?.secondname,
      phone: user?.phone || '',
      phoneExt: ''
    }),
    [user]
  )

  const getCustomerContact = useCallback(() => {
    const contact: ReturnType<() => CustomerContact> = {
      contact: userContact,
      type: OrderItemClient.type.INDIVIDUAL
    }

    if (selectedCustomer?.type === OrderItemClient.type.LEGAL) {
      contact.company = { name: selectedCustomer?.name, inn: selectedCustomer?.inn, kpp: selectedCustomer?.kpp }
      contact.type = OrderItemClient.type.LEGAL

      return contact
    }

    return contact
  }, [selectedCustomer?.inn, selectedCustomer?.kpp, selectedCustomer?.name, selectedCustomer?.type, userContact])

  const getPartnerContact = (partner: TOrderCreatePartnerFormData): CustomerContact => {
    const isUser = partner?.isUser === RadioSwitchValue.yes
    const contact: CustomerContact = {
      contact: {
        firstname: isUser ? userContact.firstname : partner?.contact?.firstname || '',
        lastname: isUser ? userContact.lastname : partner?.contact?.lastname || '',
        secondname: isUser ? userContact.secondname : partner?.contact?.secondname || '',
        phone: isUser ? userContact.phone : partner?.contact?.phone || '',
        phoneExt: isUser ? '' : partner?.contact?.phoneExt
      },
      type: OrderItemClient.type.INDIVIDUAL
    }

    if (isUser && selectedCustomer?.type === OrderItemClient.type.LEGAL) {
      contact.company = {
        name: selectedCustomer?.name,
        inn: selectedCustomer?.inn,
        kpp: selectedCustomer?.kpp
      }
      contact.type = OrderItemClient.type.LEGAL

      return contact
    }

    if (!isUser && partner?.type === OrderItemClient.type.LEGAL) {
      contact.company = {
        name: partner?.company?.name || '',
        inn: partner?.company?.inn || '',
        kpp: partner?.company?.kpp || ''
      }
      contact.type = OrderItemClient.type.LEGAL

      return contact
    }

    return contact
  }

  return (
    <OrderCreateContext.Provider
      value={{
        activeCustomerId,
        setActiveCustomerId,
        activeCustomer,
        setActiveCustomer,
        selectedCustomer,
        setSelectedCustomer,
        isContactsByCustomerList,
        setIsContactsByCustomerList,
        isActiveCustomerAddContactAccess,
        setIsActiveCustomerAddContactAccess,
        isShowAttachBtn,
        setIsShowAttachBtn,
        isAttachBlockFullHeight,
        setIsAttachBlockFullHeight,
        getCustomerContact,
        getPartnerContact
      }}
    >
      {children}
    </OrderCreateContext.Provider>
  )
}

const useOrderCreateFormData = () => useContext(OrderCreateContext)

export { OrderCreateContext, OrderCreateProvider, useOrderCreateFormData }
